import { useState } from 'react'

import { useExecuteCommandMutation } from '@redux/api'

import { getAllPagesData } from '@helpers'
import type { ObjectDataRecord } from '@types'

import { buildCommandRequestData } from '../lib/buildCommandRequestData'
import { PrintFormConfig } from '../types'

export const useExecutePrintFormCommand = () => {
  const [executeCommand] = useExecuteCommandMutation()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const executeWithTimeout = async (
    commandParams: Parameters<typeof executeCommand>[0],
    timeoutMs = 20000
  ): Promise<unknown> => {
    const request = executeCommand(commandParams)
    const timeoutId = setTimeout(() => {
      try {
        request.abort()
      } catch {
        // noop
      }
    }, timeoutMs)

    try {
      return await request.unwrap()
    } finally {
      clearTimeout(timeoutId)
    }
  }

  const loadRecords = async (config: PrintFormConfig): Promise<ObjectDataRecord[] | null> => {
    setError(null)
    setIsLoading(true)

    const { command } = config
    const { params, body } = buildCommandRequestData(command)

    const commandParams = {
      objectCode: command.objectCode,
      commandCode: command.commandCode,
      method: command.method ?? 'POST',
      resource: command.resource,
      params,
      body,
      filter: command.filter,
      transformResponseWithPagination: true,
      size: 1000,
    }

    try {
      const firstPageData = await executeWithTimeout(commandParams)

      const records = await getAllPagesData({
        firstPageData: firstPageData as { data: ObjectDataRecord[]; totalCount: number },
        executeCommand: payload => executeCommand(payload),
        commandParams,
      })

      if (records.length === 0) {
        setError('Нет данных для печати: команда вернула 0 записей')
      }

      return records
    } catch {
      setError('Ошибка загрузки данных (таймаут или ошибка API)')

      return null
    } finally {
      setIsLoading(false)
    }
  }

  return { loadRecords, isLoading, error }
}
